import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api, { openAuthenticatedFile } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { usePageTitle } from '../context/PageChromeContext';
import {
  FiArrowLeft,
  FiClock,
  FiDownload,
  FiEdit,
  FiExternalLink,
  FiFileText,
  FiMail,
  FiPhone,
  FiRefreshCw,
  FiSave,
  FiX,
} from 'react-icons/fi';
import IconButton from '../components/IconButton';
import LoadingButton, { ButtonSpinner } from '../components/LoadingButton';
import SendTemplateEmailModal from '../components/SendTemplateEmailModal';
import RegisterOnboardingFlow from '../components/RegisterOnboardingFlow';
import ProfileSupportTickets from '../components/ProfileSupportTickets';
import { formatLocalDateTime } from '../utils/formatTimestamp';
import { appAlert } from '../utils/appDialog';
import {
  REGISTER_STATUS_OPTIONS,
  normalizeRegisterStatus,
  statusLabel,
  statusTone,
} from './registerShared';
import './CandidateDetails.css';
import './RegisterCandidates.css';

const emptyForm = {
  fname: '',
  lname: '',
  email: '',
  phone: '',
  position: '',
  service: '',
  start_date: '',
  price: '',
  ref: '',
  status: 'interested',
  private_notes: '',
  follow_up_at: '',
};

function rowToForm(row) {
  if (!row) return { ...emptyForm };
  return {
    fname: row.fname || '',
    lname: row.lname || '',
    email: row.email || '',
    phone: row.phone || '',
    position: row.position || '',
    service: row.service || '',
    start_date: row.start_date ? String(row.start_date).slice(0, 10) : '',
    price: row.price != null && row.price !== '' ? String(row.price) : '',
    ref: row.ref || '',
    status: normalizeRegisterStatus(row.status),
    private_notes: row.private_notes || '',
    follow_up_at: row.follow_up_at ? String(row.follow_up_at).slice(0, 10) : '',
  };
}

function logActionClass(action) {
  if (['create', 'update', 'delete', 'view', 'email'].includes(action)) return action;
  return 'unknown';
}

function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function badgeClassForStatus(status) {
  const tone = statusTone(status);
  if (tone === 'success') return 'success';
  if (tone === 'info') return 'info';
  if (tone === 'danger') return 'danger';
  return 'muted';
}

const RegisterCandidateDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [flowBusy, setFlowBusy] = useState(null);

  const registerId = id ? parseInt(id, 10) : NaN;
  const canAccess =
    user?.role === 'admin' ||
    user?.role === 'consultant' ||
    hasPermission('tab_register');

  const {
    data: detail,
    isLoading: detailLoading,
    isError,
    error,
  } = useQuery(
    ['register-candidates', registerId],
    () => api.get(`/register-candidates/${registerId}`).then((r) => r.data),
    { enabled: Boolean(canAccess && user && Number.isFinite(registerId)), staleTime: 0 }
  );

  const {
    data: historyPayload,
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = useQuery(
    ['register-history', registerId],
    () => api.get(`/register-candidates/${registerId}/history?limit=80`).then((r) => r.data),
    { enabled: Boolean(canAccess && user && Number.isFinite(registerId)), staleTime: 10_000 }
  );

  const historyItems = useMemo(
    () => historyPayload?.items || historyPayload || [],
    [historyPayload]
  );

  usePageTitle(
    detail
      ? `${detail.fname || ''} ${detail.lname || ''}`.trim() || detail.email || 'Register profile'
      : 'Register profile'
  );

  useEffect(() => {
    if (detail) setForm(rowToForm(detail));
  }, [detail]);

  const saveMutation = useMutation(
    (body) => api.patch(`/register-candidates/${registerId}`, body),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['register-candidates']);
        queryClient.setQueryData(['register-candidates', registerId], res.data);
        queryClient.invalidateQueries(['register-history', registerId]);
        setIsEditing(false);
        void appAlert('Register profile saved.');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const handleStartEdit = () => {
    if (detail) setForm(rowToForm(detail));
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (detail) setForm(rowToForm(detail));
    setIsEditing(false);
  };

  const handleSave = (e) => {
    e?.preventDefault?.();
    if (!Number.isFinite(registerId)) return;
    saveMutation.mutate({
      fname: form.fname,
      lname: form.lname,
      email: form.email,
      phone: form.phone,
      position: form.position,
      service: form.service,
      start_date: form.start_date || null,
      price: form.price === '' ? null : form.price,
      ref: form.ref,
      status: form.status,
      private_notes: form.private_notes,
      follow_up_at: form.follow_up_at || null,
    });
  };

  const downloadResume = async () => {
    try {
      if (detail?.resume_url) {
        window.open(detail.resume_url, '_blank', 'noopener,noreferrer');
        return;
      }
      await openAuthenticatedFile(`/register-candidates/${registerId}/resume`);
    } catch (err) {
      void appAlert(err?.response?.data?.error || err?.message || 'Could not download resume');
    }
  };

  const refreshRegister = (resData) => {
    if (resData) queryClient.setQueryData(['register-candidates', registerId], resData);
    queryClient.invalidateQueries(['register-candidates']);
    queryClient.invalidateQueries(['register-history', registerId]);
  };

  const markQuestionnaireSent = async () => {
    setFlowBusy('q-sent');
    try {
      const res = await api.post(`/register-candidates/${registerId}/questionnaire/mark-sent`);
      refreshRegister(res.data);
      void appAlert('Questionnaire marked as sent.');
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
    } finally {
      setFlowBusy(null);
    }
  };

  const markQuestionnaireCompleted = async () => {
    setFlowBusy('q-done');
    try {
      const res = await api.post(
        `/register-candidates/${registerId}/questionnaire/mark-completed`
      );
      refreshRegister(res.data);
      void appAlert('Questionnaire marked complete.');
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
    } finally {
      setFlowBusy(null);
    }
  };

  const clearQuestionnaireCompleted = async () => {
    setFlowBusy('q-undo');
    try {
      const res = await api.post(
        `/register-candidates/${registerId}/questionnaire/mark-completed`,
        { undo: true }
      );
      refreshRegister(res.data);
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
    } finally {
      setFlowBusy(null);
    }
  };

  if (loading) return <div className="loading">Loading…</div>;
  if (!canAccess) return <Navigate to="/" replace />;
  if (!Number.isFinite(registerId)) return <Navigate to="/register" replace />;

  if (detailLoading && !detail) {
    return <div className="loading">Loading register profile…</div>;
  }

  if (isError || !detail) {
    return (
      <div className="candidate-details list-page register-details-page">
        <div className="candidate-details-toolbar">
          <IconButton
            icon={FiArrowLeft}
            label="Back to register"
            onClick={() => navigate('/register')}
          />
        </div>
        <div className="candidate-details-card">
          <div className="error">
            {error?.response?.data?.error || error?.message || 'Register profile not found.'}
          </div>
          <Link to="/register" className="btn btn-secondary" style={{ marginTop: '1rem' }}>
            Back to Register
          </Link>
        </div>
      </div>
    );
  }

  const displayName =
    `${form.fname || ''} ${form.lname || ''}`.trim() || form.email || `Register #${registerId}`;
  const hasResume = Boolean(detail.has_resume || detail.resume_r2_key || detail.resume_url);
  const viewStatus = normalizeRegisterStatus(form.status);
  const sizeLabel = formatFileSize(detail.resume_file_size);

  return (
    <div className="candidate-details list-page register-details-page">
      <div className="candidate-details-toolbar page-toolbar">
        <IconButton
          icon={FiArrowLeft}
          label="Back to register"
          onClick={() => navigate('/register')}
        />
      </div>

      <div className="candidate-details-card candidate-details-hero">
        <div className="candidate-details-header">
          <div className="candidate-details-identity">
            <h1>{displayName}</h1>
            <div className="candidate-details-contact">
              {form.email ? (
                <span className="candidate-details-contact-item">
                  <FiMail aria-hidden /> {form.email}
                </span>
              ) : null}
              {form.phone ? (
                <span className="candidate-details-contact-item">
                  <FiPhone aria-hidden /> {form.phone}
                </span>
              ) : null}
            </div>
          </div>
          <div className="list-page-header-actions candidate-details-actions">
            <span className={`badge badge-${badgeClassForStatus(viewStatus)}`}>
              {statusLabel(viewStatus)}
            </span>
            {form.email ? (
              <IconButton
                icon={FiMail}
                label="Send email"
                size="sm"
                onClick={() => setShowSendEmail(true)}
              />
            ) : null}
            {!isEditing ? (
              <IconButton
                icon={FiEdit}
                label="Edit profile"
                variant="primary"
                size="sm"
                onClick={handleStartEdit}
              />
            ) : null}
          </div>
        </div>

        {detail.created_user_id ? (
          <div className="candidate-assignment-panel">
            <p className="candidate-assignment-label">
              <strong>Linked candidate</strong>
            </p>
            <div className="candidate-assignment-row">
              <span>Platform user #{detail.created_user_id}</span>
              <Link to={`/candidates/${detail.created_user_id}`} className="btn btn-secondary btn-sm">
                Open candidate <FiExternalLink aria-hidden />
              </Link>
            </div>
          </div>
        ) : null}
      </div>

      <div className="candidate-section candidate-details-card">
        <RegisterOnboardingFlow
          onboarding={detail.onboarding}
          busyAction={flowBusy}
          onMarkQuestionnaireSent={markQuestionnaireSent}
          onMarkQuestionnaireCompleted={markQuestionnaireCompleted}
          onClearQuestionnaireCompleted={clearQuestionnaireCompleted}
          onOpenOnboard={() => navigate(`/candidates?onboard_register=${registerId}`)}
        />
      </div>

      <div className="candidate-section candidate-details-card">
        <div className="section-header">
          <h2>Profile Information</h2>
          {isEditing ? (
            <div className="edit-actions">
              <LoadingButton
                className="btn btn-success"
                icon={FiSave}
                loading={saveMutation.isLoading}
                loadingLabel="Saving…"
                onClick={handleSave}
              >
                Save
              </LoadingButton>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                <FiX /> Cancel
              </button>
            </div>
          ) : null}
        </div>

        {saveMutation.isError ? (
          <div className="error" style={{ marginBottom: '1rem' }}>
            {saveMutation.error?.response?.data?.error ||
              saveMutation.error?.message ||
              'Failed to save register profile'}
          </div>
        ) : null}

        {isEditing ? (
          <form className="profile-edit-form" onSubmit={handleSave}>
            <h3 className="profile-edit-subsection">Contact &amp; service</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="reg-fname">First name</label>
                <input
                  id="reg-fname"
                  value={form.fname}
                  onChange={(e) => setForm({ ...form, fname: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="reg-lname">Last name</label>
                <input
                  id="reg-lname"
                  value={form.lname}
                  onChange={(e) => setForm({ ...form, lname: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="reg-email">Email</label>
                <input
                  id="reg-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="reg-phone">Phone</label>
                <input
                  id="reg-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="reg-position">Position</label>
                <input
                  id="reg-position"
                  value={form.position}
                  onChange={(e) => setForm({ ...form, position: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="reg-service">Service</label>
                <input
                  id="reg-service"
                  value={form.service}
                  onChange={(e) => setForm({ ...form, service: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="reg-start">Start date</label>
                <input
                  id="reg-start"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="reg-price">Price</label>
                <input
                  id="reg-price"
                  type="number"
                  step="any"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="reg-ref">Ref</label>
              <input
                id="reg-ref"
                value={form.ref}
                onChange={(e) => setForm({ ...form, ref: e.target.value })}
              />
            </div>

            <h3 className="profile-edit-subsection">Enrollment status</h3>
            <p className="form-hint" style={{ marginTop: 0 }}>
              Mark interest after outreach. Disabled stops contact. Future schedules follow-up.
              Created is set when onboarded as a platform candidate.
            </p>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="reg-status">Status</label>
                <select
                  id="reg-status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {REGISTER_STATUS_OPTIONS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label} — {s.hint}
                    </option>
                  ))}
                </select>
              </div>
              {form.status === 'future' ? (
                <div className="form-group">
                  <label htmlFor="reg-followup">Follow-up date</label>
                  <input
                    id="reg-followup"
                    type="date"
                    value={form.follow_up_at}
                    onChange={(e) => setForm({ ...form, follow_up_at: e.target.value })}
                  />
                </div>
              ) : null}
            </div>
            <div className="form-group">
              <label htmlFor="reg-notes">Private notes (internal only)</label>
              <textarea
                id="reg-notes"
                rows={4}
                value={form.private_notes}
                onChange={(e) => setForm({ ...form, private_notes: e.target.value })}
                placeholder="Call notes, interest signals, objections — never shown externally."
              />
            </div>
          </form>
        ) : (
          <div className="profile-grid">
            <div className="profile-item">
              <strong>First name</strong>
              <p>{form.fname || '—'}</p>
            </div>
            <div className="profile-item">
              <strong>Last name</strong>
              <p>{form.lname || '—'}</p>
            </div>
            <div className="profile-item">
              <strong>Email</strong>
              <p>{form.email || '—'}</p>
            </div>
            <div className="profile-item">
              <strong>Phone</strong>
              <p>{form.phone || '—'}</p>
            </div>
            <div className="profile-item">
              <strong>Position</strong>
              <p>{form.position || '—'}</p>
            </div>
            <div className="profile-item">
              <strong>Service</strong>
              <p>{form.service || '—'}</p>
            </div>
            <div className="profile-item">
              <strong>Start date</strong>
              <p>{form.start_date || '—'}</p>
            </div>
            <div className="profile-item">
              <strong>Price</strong>
              <p>{form.price !== '' && form.price != null ? form.price : '—'}</p>
            </div>
            <div className="profile-item">
              <strong>Ref</strong>
              <p>{form.ref || '—'}</p>
            </div>
            <div className="profile-item">
              <strong>Status</strong>
              <p>
                <span className={`register-status-badge register-status-badge--${statusTone(viewStatus)}`}>
                  {statusLabel(viewStatus)}
                </span>
              </p>
            </div>
            {viewStatus === 'future' ? (
              <div className="profile-item">
                <strong>Follow-up date</strong>
                <p>{form.follow_up_at || '—'}</p>
              </div>
            ) : null}
            <div className="profile-item full-width">
              <strong>Private notes</strong>
              <p>{form.private_notes || 'No notes yet.'}</p>
            </div>
          </div>
        )}
      </div>

      <div className="candidate-section candidate-details-card">
        <div className="section-header">
          <h2>
            <FiFileText aria-hidden /> Resume {hasResume ? '(1)' : '(0)'}
          </h2>
        </div>
        {hasResume ? (
          <div className="resumes-list">
            <div className="resume-item">
              <div className="resume-item-header">
                <h4>{detail.resume_file_name || 'Resume'}</h4>
                <span className="resume-meta">
                  {sizeLabel ? <span>{sizeLabel}</span> : null}
                </span>
              </div>
              {detail.resume_r2_key ? (
                <p className="register-resume-key" title={detail.resume_r2_key}>
                  R2: {detail.resume_r2_key}
                </p>
              ) : detail.resume_url ? (
                <p>External URL attached</p>
              ) : null}
              <p>
                When this person is created as a candidate, this resume is linked into their profile
                automatically.
              </p>
              <button type="button" className="btn btn-secondary btn-sm" onClick={downloadResume}>
                <FiDownload /> Download
              </button>
            </div>
          </div>
        ) : (
          <p className="profile-empty-state">
            No resume on this registration. External intake can store an R2 object key (
            <code>resume_r2_key</code>) when the form uploads a CV.
          </p>
        )}
      </div>

      <ProfileSupportTickets
        email={form.email || detail.email}
        entityType="register_candidate"
        entityId={registerId}
        limit={5}
      />

      <div className="candidate-section candidate-details-card activity-history">
        <div className="activity-history-header">
          <h2>
            <FiClock aria-hidden /> Activity &amp; email history
          </h2>
          <div className="activity-history-controls">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => refetchHistory()}
              disabled={historyLoading}
            >
              {historyLoading ? <ButtonSpinner /> : <FiRefreshCw aria-hidden />} Refresh
            </button>
          </div>
        </div>

        {historyLoading ? (
          <p>Loading activity history…</p>
        ) : historyItems.length > 0 ? (
          <>
            <div className="activity-stats">
              <span className="activity-count">
                Showing {historyItems.length} event{historyItems.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="activity-list">
              {historyItems.map((log) => {
                const action = log.kind === 'email' ? 'email' : log.action;
                return (
                  <div
                    key={log.id}
                    className={`activity-item activity-item--${logActionClass(action)}`}
                  >
                    <div className="activity-header">
                      <div className="activity-action">
                        <span className={`activity-badge activity-${logActionClass(action)}`}>
                          {action}
                        </span>
                        <span className="activity-description">
                          {log.description || '—'}
                        </span>
                      </div>
                      <div className="activity-meta">
                        <span className="activity-time">{formatLocalDateTime(log.timestamp)}</span>
                        {log.field_name ? <span>{log.field_name}</span> : null}
                        {log.status ? <span>{log.status}</span> : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <p className="profile-empty-state">
            No activity yet. Profile edits, status changes, and emails sent to this person appear
            here.
          </p>
        )}
      </div>

      <SendTemplateEmailModal
        open={showSendEmail}
        onClose={() => {
          setShowSendEmail(false);
          queryClient.invalidateQueries(['register-history', registerId]);
        }}
        recipient={{
          registerCandidateId: registerId,
          email: form.email,
          firstName: form.fname,
          lastName: form.lname,
        }}
      />
    </div>
  );
};

export default RegisterCandidateDetails;
